import type { ReactNode } from 'react'
import { Check, Loader2 } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import panel from './SettingsSection.module.css'

/**
 * A section footer that always says whether changes are saved, with Discard
 * and Save. It stays in view while there is something to save.
 */
export function SettingsSaveFooter({
  dirty,
  saving,
  saved,
  error,
  problem,
  canSave,
  saveLabel = 'Save changes',
  onDiscard,
  onSave,
  start,
}: {
  dirty: boolean
  saving: boolean
  /** The last save succeeded and nothing changed since. */
  saved: boolean
  /** Why the last save failed, kept until the next change. */
  error?: string | null
  /** Why the current changes can't be saved yet. */
  problem?: string | null
  canSave: boolean
  saveLabel?: string
  onDiscard: () => void
  onSave: () => void
  /** Actions shown before the status, such as "Add age range". */
  start?: ReactNode
}) {
  return (
    <footer className={panel.panelFooter} data-dirty={dirty || undefined}>
      {start ? <div className={panel.footerStart}>{start}</div> : null}
      <div className={panel.footerStatus} aria-live="polite">
        {saving ? (
          <span className={panel.statusLine}>
            <Loader2 size={15} className={panel.spin} aria-hidden="true" />{' '}
            Saving…
          </span>
        ) : error ? (
          <span className={panel.statusLine} data-tone="error">
            {error}
          </span>
        ) : dirty ? (
          <span className={panel.statusLine} data-tone="dirty">
            {problem ? `Unsaved changes · ${problem}` : 'Unsaved changes'}
          </span>
        ) : saved ? (
          <span className={panel.statusLine} data-tone="saved">
            <Check size={16} aria-hidden="true" /> All changes saved
          </span>
        ) : (
          <span className={panel.statusLine}>No unsaved changes</span>
        )}
      </div>
      <div className={panel.footerActions}>
        <Button
          type="button"
          variant="secondary"
          disabled={!dirty || saving}
          onClick={onDiscard}
        >
          Discard changes
        </Button>
        <Button
          type="button"
          disabled={!dirty || !canSave || saving}
          onClick={onSave}
        >
          {saving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </footer>
  )
}
